import { useState, useEffect, useCallback } from 'react';
import { ConnectionState } from 'livekit-client';
import { LiveKitService, AudioCaptureMode } from '../services/LiveKitService';

export interface UseLiveKitReturn {
  isConnected: boolean;
  connectionState: ConnectionState | null;
  connect: (identity: string, roomName?: string, mode?: AudioCaptureMode) => Promise<string>;
  disconnect: () => void;
  error: Error | null;
  toggleMicrophone: () => boolean;
  isMicrophoneMuted: boolean;
  resetMicrophone: () => void;
}

export const useLiveKit = (): UseLiveKitReturn => {
  const [service] = useState(() => new LiveKitService());
  const [connectionState, setConnectionState] = useState<ConnectionState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(false);

  useEffect(() => {
    // Set up callbacks
    service.setCallbacks(
      // Connection state callback
      (state: ConnectionState) => {
        setConnectionState(state);
        switch (state) {
          case ConnectionState.Connected:
            setError(null);
            break;
          case ConnectionState.Disconnected:
          case ConnectionState.Reconnecting:
            setError(new Error('Connection issue'));
            break;
        }
      },
      // Error callback
      (error: Error) => {
        setError(error);
        console.error('LiveKit error:', error);
      }
    );

    // Cleanup on unmount
    return () => {
      service.disconnect();
    };
  }, [service]);

  const connect = useCallback(async (identity: string, roomName?: string, mode?: AudioCaptureMode): Promise<string> => {
    try {
      setError(null);
      const room = await service.connect(identity, roomName, mode);
      return room;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to connect'));
      throw err;
    }
  }, [service]);

  const disconnect = useCallback(() => {
    service.disconnect();
  }, [service]);

  const toggleMicrophone = useCallback(() => {
    const isMuted = service.toggleMicrophone();
    setIsMicrophoneMuted(isMuted);
    return isMuted;
  }, [service]);

  const resetMicrophone = useCallback(() => {
    setIsMicrophoneMuted(false);
    service.resetMicrophone();
  }, [service]);

  return {
    isConnected: service.isConnected(),
    connectionState,
    connect,
    disconnect,
    error,
    toggleMicrophone,
    isMicrophoneMuted,
    resetMicrophone
  };
};
